import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { AppDispatch, RootState } from '../redux/store';
import { fetchAdminPrompts } from '../prompts/redux/promptsSlice';
import { Section } from '../shared/components/Section';
import { GlowAlert } from '../shared/components/GlowAlert';

const PromptsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { prompts, status, error } = useSelector((state: RootState) => state.prompts);

  useEffect(() => {
    dispatch(fetchAdminPrompts());
  }, [dispatch]);

  const edit = (id: string) => navigate(`/prompts/${encodeURIComponent(id)}/edit`);

  return (
    <Section
      title="System prompts"
      onRefresh={() => dispatch(fetchAdminPrompts())}
      refreshing={status === 'loading'}
    >
      {error && (
        <GlowAlert sx={{ mb: 2 }}>
          {error}
        </GlowAlert>
      )}
      {status === 'loading' && prompts.length === 0 && <GlowAlert severity="info">Loading prompts...</GlowAlert>}

      {prompts.length > 0 && (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Prompt</TableCell>
                <TableCell>Key</TableCell>
                <TableCell align="right">Version</TableCell>
                <TableCell align="right">Characters</TableCell>
                <TableCell>Last updated</TableCell>
                <TableCell>Updated by</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {prompts.map((prompt) => (
                <TableRow key={prompt.id} hover sx={{ cursor: 'pointer' }} onClick={() => edit(prompt.id)}>
                  <TableCell>{prompt.label}</TableCell>
                  <TableCell>
                    <code>{prompt.key}</code>
                  </TableCell>
                  <TableCell align="right">{prompt.version ?? '—'}</TableCell>
                  <TableCell align="right">{prompt.content.length.toLocaleString()}</TableCell>
                  <TableCell>{prompt.updated_at ? new Date(prompt.updated_at).toLocaleString() : '—'}</TableCell>
                  <TableCell>{prompt.updated_by || '—'}</TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        edit(prompt.id);
                      }}
                    >
                      Edit
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {status !== 'loading' && !error && prompts.length === 0 && (
        <Typography color="text.secondary">No prompts found.</Typography>
      )}
    </Section>
  );
};

export default PromptsPage;
